import { useMemo } from 'react'

import { useOpenWindow, useWindow } from '@krp/client-runtime'
import { platformMyResources } from '@krp/platform-ui'
import {
  AQSI_WINDOWS,
  aqsiReceipt,
  aqsiReceiptRegisters,
  makeSelectReceiptDetailVm,
  receiptsReloadRequested,
  ReceiptDetailScreen,
  type ReceiptArgs,
  type ReceiptDetailActions,
} from '@krp/resource-aqsi-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { AQSI_MINE } from './aqsiMine'

/** Окно чека AQSI поверх экрана: rid и номер чека — из адреса окна; два AQSI с одним номером чека — разные записи кэша. */
export function ReceiptDetailBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  const resourceId = window.params.rid ?? ''
  const receiptId = window.params.key ?? ''
  const args = useMemo<ReceiptArgs>(() => ({ resourceId, receiptId }), [resourceId, receiptId])
  aqsiReceipt.useQuerySubscription(args, { skip: resourceId === '' || receiptId === '' })
  // UC1d: «возвраты посчитаны» — признак журнала этого rid (`capabilities.refunds`), детализация его не несёт.
  const registersArgs = useMemo(() => ({ resourceId }), [resourceId])
  aqsiReceiptRegisters.useQuerySubscription(registersArgs, { skip: resourceId === '' })

  const selectVm = useMemo(() => {
    const selectPackageVm = makeSelectReceiptDetailVm()
    const selectQuery = aqsiReceipt.select(args)
    const selectRegisters = aqsiReceiptRegisters.select(registersArgs)
    return (state: RootState) =>
      selectPackageVm({ query: selectQuery(state), refundsKnown: selectRegisters(state).data?.state.refundsKnown })
  }, [args, registersArgs])
  const vm = useAppSelector(selectVm)
  // «Обмен с AQSI» — только с правом `operations.read` на этом кабинете; пока список не пришёл, кнопки нет.
  const exchangeReaders = platformMyResources.useQuery(AQSI_MINE.exchanges)
  const exchangeAllowed = exchangeReaders.data?.some((source) => source.id.toLowerCase() === resourceId.toLowerCase()) === true

  const actions = useMemo<ReceiptDetailActions>(
    () => ({
      reload: () => dispatch(receiptsReloadRequested({ resourceId })),
      close: window.close,
      openReceipt: (id: string) => openWindow(AQSI_WINDOWS.receipt, { rid: resourceId, key: id }),
      openGood: (key: string) => openWindow(AQSI_WINDOWS.good, { rid: resourceId, key }),
      refund: () => openWindow(AQSI_WINDOWS.refund, { rid: resourceId, key: receiptId }),
      ...(exchangeAllowed ? { openExchange: (saleId: string) => openWindow(AQSI_WINDOWS.saleExchange, { rid: resourceId, saleId }) } : {}),
    }),
    [dispatch, exchangeAllowed, openWindow, receiptId, resourceId, window.close],
  )
  return <ReceiptDetailScreen vm={vm} actions={actions} />
}
